import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarBlank, MapPin, VideoCamera, Clock, ArrowRight, Globe } from "@phosphor-icons/react";

const KIND_LABEL = {
  annual: "Annual meet",
  quarterly_regional: "Regional · quarterly",
  virtual: "Virtual town hall",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function PublicMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/meetings/public").then(({ data }) => {
      setMeetings(data.meetings || []);
    }).finally(() => setLoading(false));
  }, []);

  const upcoming = meetings.filter((m) => !m.starts_at || new Date(m.starts_at) >= new Date());
  const past = meetings.filter((m) => m.starts_at && new Date(m.starts_at) < new Date());

  return (
    <section className="max-w-7xl mx-auto px-6 py-16 sm:py-24" data-testid="public-meetings-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
        <div className="md:col-span-7">
          <div className="overline mb-4">Events</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black text-navy tracking-tighter">
            Where the network<br />
            <span className="text-ember">actually meets.</span>
          </h1>
        </div>
        <div className="md:col-span-5">
          <p className="text-navy/70">
            EPAS runs one annual in-person meeting and four quarterly regional sessions a year. Members talk
            face-to-face. Trade lanes get built. Cooperation gets real. This is the public calendar — full
            agenda and venue details are shared with approved members.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-navy/60">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="bg-white border border-dashed border-black/15 p-10 text-center text-navy/60">
          The next event will be announced shortly. Want to be notified? <Link to="/apply" className="text-ember hover:underline">Apply for membership →</Link>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="mb-16">
              <div className="overline mb-4">Upcoming · {upcoming.length}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="meetings-upcoming">
                {upcoming.map((m) => <Card key={m.id} m={m} />)}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div className="overline mb-4">Past · {past.length}</div>
              <ul className="bg-white border border-black/10 divide-y divide-black/5">
                {past.map((m) => (
                  <li key={m.id} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`meeting-past-${m.id}`}>
                    <div>
                      <div className="font-medium text-navy">{m.title}</div>
                      <div className="text-xs text-navy/60">{fmt(m.starts_at)} · {m.city ? `${m.city}, ${m.country}` : KIND_LABEL[m.kind]}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-navy/40">{KIND_LABEL[m.kind] || m.kind}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="mt-20 border border-black/10 bg-white p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-8">
          <div className="overline mb-3">Members only</div>
          <h3 className="font-display text-3xl font-bold text-navy">Want a seat at the table?</h3>
          <p className="text-navy/70 mt-2">Apply for membership — agendas, venues and member RSVPs are shared with approved partners only.</p>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-3">
          <Link to="/apply" className="btn-primary" data-testid="meetings-apply-cta">Apply <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>
  );
}

const Card = ({ m }) => {
  const inPerson = m.format === "in_person" || m.format === "hybrid";
  return (
    <div className="bg-white border border-black/10 p-6 flex flex-col" data-testid={`meeting-upcoming-${m.id}`}>
      <div className="flex items-center justify-between">
        <span className="overline">{KIND_LABEL[m.kind] || m.kind}</span>
        {inPerson ? <MapPin size={18} weight="duotone" className="text-ember" /> : <VideoCamera size={18} weight="duotone" className="text-ember" />}
      </div>
      <h3 className="mt-2 font-display text-2xl font-bold text-navy leading-tight">{m.title}</h3>
      <div className="mt-3 space-y-1 text-sm text-navy/70">
        {m.starts_at && (
          <div className="flex items-center gap-2"><Clock size={14} /> {fmt(m.starts_at)}{m.ends_at ? ` — ${fmt(m.ends_at)}` : ""}</div>
        )}
        {(m.city || m.country) && inPerson && (
          <div className="flex items-center gap-2"><MapPin size={14} /> {m.city}{m.city && m.country ? ", " : ""}{m.country}{m.venue ? ` · ${m.venue}` : ""}</div>
        )}
        {!inPerson && (
          <div className="flex items-center gap-2"><Globe size={14} /> {m.format === "teams" ? "Microsoft Teams" : "Zoom"} {m.region && `· ${m.region}`}</div>
        )}
      </div>
      {m.description && <p className="mt-4 text-sm text-navy/70 leading-relaxed flex-1">{m.description}</p>}
      <div className="mt-4 pt-3 border-t border-black/10 text-[10px] uppercase tracking-widest text-navy/40">
        Full agenda visible to approved members
      </div>
    </div>
  );
};
